import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation } from '@apollo/client';
import { Stack, styled } from '@mui/material';
import { theme } from '~/shared/styles/theme';
import { ROUTES } from '~/shared/constants';
import { AUTH_STATUS } from '~/graphql/queries';
import { RECORD_LOGIN } from '~/graphql/mutations';
import { reportError } from '~/shared/utils/sentry';

/**
 * SponsorRouteGuard protects sponsor routes by ensuring the user:
 * 1. Is authenticated via Auth0
 * 2. Has sponsor permissions (isSponsor: true from authStatus query)
 *
 * If either check fails, redirects to login page.
 */
export const SponsorRouteGuard = () => {
  const { isAuthenticated, isLoading: auth0Loading } = useAuth0();

  const { data, loading: authLoading } = useQuery(AUTH_STATUS, {
    fetchPolicy: 'network-only',
  });

  const isSponsor = Boolean(data?.authStatus?.isSponsor);
  const isSponsorAuthenticated = isAuthenticated && isSponsor;
  const isLoading = auth0Loading || authLoading;

  const [recordLogin] = useMutation(RECORD_LOGIN);

  useEffect(() => {
    if (isSponsorAuthenticated && !sessionStorage.getItem('loginRecorded')) {
      sessionStorage.setItem('loginRecorded', '1');
      (async () => {
        try {
          await recordLogin();
        } catch (error) {
          reportError(error, { operation: 'recordLogin' });
        }
      })();
    }
  }, [isSponsorAuthenticated, recordLogin]);

  if (isLoading) {
    return (
      <LoadingContainer
        role="status"
        aria-label="Checking authentication"
        data-testid="sponsor-route-loading"
      />
    );
  }

  if (!isAuthenticated || !isSponsor) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return <Outlet />;
};

const LoadingContainer = styled(Stack)({
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: theme.palette.custom.cream,
});
